import { useState, useCallback, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Map, Building2, MapPin, Gift } from "lucide-react";
import styles from "./CountryManager.module.css";
import AddCountryModal from "../../../components/GeoData/Countries/AddCountryModal";
import CountryHolidayTypesModal from "../../../components/GeoData/Countries/CountryHolidayTypesModal";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import { useToast } from "../../../context/ToastContext";
import {
  useGetCountries,
  useCreateCountry,
  useUpdateCountry,
} from "../../../api/geo/useCountries";
import { assignHolidayTypesToCountry } from "../../../api/geo/useHolidayTypes";
import { flagSrcForCountry } from "../../../utils/countryFlags";
import { geoCascadeMessage } from "../../../utils/geoCascadeMessage";

// Pending toggle changes: { [rowId]: { euMember?, vatCountry?, active? } }
const getDisplayValue = (row, field, pendingChanges) =>
  pendingChanges[row.id]?.[field] ?? row[field];

const CountryManager = () => {
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [euFilter, setEuFilter] = useState("");
  const [vatFilter, setVatFilter] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [holidayTypesRow, setHolidayTypesRow] = useState(null);

  const [pendingChanges, setPendingChanges] = useState({});
  const [toggleSaveLoading, setToggleSaveLoading] = useState(false);

  const { data: rows, pagination, loading, error, refetch } = useGetCountries({
    page,
    limit,
    search,
    active: activeFilter,
    sortBy: "name",
    sortOrder: "ASC",
  });

  const { execute: createCountry, loading: createLoading } = useCreateCountry();
  const { execute: updateCountry, loading: updateLoading } = useUpdateCountry();

  const saveLoading = createLoading || updateLoading;
  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  // Build page numbers for pagination: [1, 2, 3, ..., last]
  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages ?? 1;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const current = page;
    const pages = new Set([1, total]);
    // Always show current and neighbors
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) {
      pages.add(i);
    }
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  }, [pagination?.totalPages, page]);

  // Real flag image from the ISO-2 code (emoji flags don't render on Windows).
  // Non-ISO codes (e.g. Northern Cyprus "NY") fall back to a bundled asset.
  const flagUrl = (row) => flagSrcForCountry(row);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (euFilter === "true" && !getDisplayValue(row, "euMember", pendingChanges)) return false;
      if (euFilter === "false" && getDisplayValue(row, "euMember", pendingChanges)) return false;
      if (vatFilter === "true" && !getDisplayValue(row, "vatCountry", pendingChanges)) return false;
      if (vatFilter === "false" && getDisplayValue(row, "vatCountry", pendingChanges)) return false;
      return true;
    });
  }, [rows, euFilter, vatFilter, pendingChanges]);

  const applyToggle = useCallback((row, field) => {
    setPendingChanges((prev) => {
      const current = prev[row.id] ?? {};
      const serverVal = row[field];
      const pendingVal = current[field];
      const nextVal = pendingVal !== undefined ? !pendingVal : !serverVal;
      return {
        ...prev,
        [row.id]: { ...current, [field]: nextVal },
      };
    });
  }, []);

  // Auto-search: debounce the input so the list filters as you type (no button).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
      setPendingChanges({});
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleAdd = () => {
    setMode("add");
    setSelectedRow(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setMode("edit");
    setSelectedRow(row);
    setShowModal(true);
  };

  const handleViewHolidayTypes = (row) => {
    setHolidayTypesRow(row);
  };

  const handleSaveToggles = async () => {
    const ids = Object.keys(pendingChanges);
    if (ids.length === 0) return;
    setToggleSaveLoading(true);
    try {
      // Switching a country off closes every hotel in it, so tally what the API reports and
      // say so — silently deactivating thousands of hotels would be a nasty surprise.
      let switchedOff = 0;
      let restored = 0;
      for (const id of ids) {
        const patch = pendingChanges[id];
        const row = rows.find((r) => String(r.id) === String(id));
        const payload = {
          euMember: patch.euMember ?? row?.euMember,
          vatCountry: patch.vatCountry ?? row?.vatCountry,
          active: patch.active ?? row?.active,
        };
        const res = await updateCountry(id, payload);
        switchedOff += res?.hotelCascade?.switchedOff || 0;
        restored += res?.hotelCascade?.restored || 0;
      }
      setPendingChanges({});
      refetch();
      showToast(geoCascadeMessage({ switchedOff, restored }), "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save changes", "error");
    } finally {
      setToggleSaveLoading(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      const { holidayTypeIds = [], ...countryPayload } = payload;

      if (mode === "add") {
        const res = await createCountry(countryPayload);
        const newCountryId = res?.data?.id;
        if (newCountryId != null && holidayTypeIds.length > 0) {
          await assignHolidayTypesToCountry(newCountryId, holidayTypeIds);
        }
        showToast("Country created successfully", "success");
      } else if (selectedRow?.id) {
        await updateCountry(selectedRow.id, countryPayload);
        await assignHolidayTypesToCountry(selectedRow.id, holidayTypeIds);
        showToast("Country updated successfully", "success");
      }
      setShowModal(false);
      refetch();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save country", "error");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Countries</div>
        <button className={styles.add} onClick={handleAdd}>
          + Add Country
        </button>
      </div>

      <div className={styles.warningBar}>
        <span className={styles.warningIcon} aria-hidden>⚠</span>
        <span>Country settings affect VAT calculation, hotel VAT rules, pricing, and reports.</span>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search by country or ISO code..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <SelectBox
          placeholder="EU Status: All Countries"
          value={euFilter}
          options={[
            { label: "All Countries", value: "" },
            { label: "EU only", value: "true" },
            { label: "Non-EU", value: "false" },
          ]}
          onChange={(e) => {
            setEuFilter(e.target.value);
            setPage(1);
            setPendingChanges({});
          }}
        />
        <SelectBox
          placeholder="VAT: All"
          value={vatFilter}
          options={[
            { label: "All", value: "" },
            { label: "VAT", value: "true" },
            { label: "Non-VAT", value: "false" },
          ]}
          onChange={(e) => {
            setVatFilter(e.target.value);
            setPage(1);
            setPendingChanges({});
          }}
        />
        <SelectBox
          placeholder="Status: All"
          value={activeFilter}
          options={[
            { label: "All", value: "" },
            { label: "Active", value: "true" },
            { label: "Inactive", value: "false" },
          ]}
          onChange={(e) => {
            setActiveFilter(e.target.value);
            setPage(1);
            setPendingChanges({});
          }}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading countries…</div>
      ) : (
        <>
          <div className={styles.resultsSummary}>
            Showing {filteredRows.length} of {pagination.total} countries
          </div>
          <div className={styles.tableSection}>
            <div className={styles.tableCard}>
              <table className={styles.table}>
              <thead>
                <tr>
                  <th>FLAG</th>
                  <th>COUNTRY</th>
                  <th>ISO</th>
                  <th>EU</th>
                  <th>VAT</th>
                  <th>STATUS</th>
                  <th>VIEW REGIONS</th>
                  <th>VIEW CITIES</th>
                  <th>VIEW ZONES</th>
                  <th>HOLIDAY TYPES</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className={styles.noResults}>
                      No countries found.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => (
                    <tr key={row.id}>
                      <td className={styles.flagCell}>
                        {flagUrl(row) ? (
                          <img
                            src={flagUrl(row)}
                            alt={`${row.name || row.code} flag`}
                            style={{ width: 28, height: 20, objectFit: "cover", borderRadius: 3, boxShadow: "0 0 0 1px rgba(0,0,0,0.08)" }}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <span className={styles.flag}>—</span>
                        )}
                      </td>
                      <td className={styles.countryName}>{row.name ?? "—"}</td>
                      <td className={styles.isoCode}>{(row.code || row.isoCode) ?? "—"}</td>
                      <td>
                        <ToggleSwitch
                          checked={!!getDisplayValue(row, "euMember", pendingChanges)}
                          onChange={() => applyToggle(row, "euMember")}
                        />
                      </td>
                      <td>
                        <ToggleSwitch
                          checked={!!getDisplayValue(row, "vatCountry", pendingChanges)}
                          onChange={() => applyToggle(row, "vatCountry")}
                        />
                      </td>
                      <td>
                        <ToggleSwitch
                          checked={!!getDisplayValue(row, "active", pendingChanges)}
                          onChange={() => applyToggle(row, "active")}
                        />
                      </td>
                      <td>
                        <Link
                          to={`/geodata/regions?countryId=${row.id}`}
                          className={styles.viewLink}
                        >
                          <Map size={14} aria-hidden />
                          <span>View regions</span>
                          <span className={styles.viewBadge}>{row.regionCount ?? 0}</span>
                        </Link>
                      </td>
                      <td>
                        <Link
                          to={`/geodata/cities?countryId=${row.id}`}
                          className={styles.viewLink}
                        >
                          <Building2 size={14} aria-hidden />
                          <span>View cities</span>
                          <span className={styles.viewBadge}>{row.cityCount ?? 0}</span>
                        </Link>
                      </td>
                      <td>
                        <Link
                          to={`/geodata/zones?countryId=${row.id}`}
                          className={styles.viewLink}
                        >
                          <MapPin size={14} aria-hidden />
                          <span>View zones</span>
                          <span className={styles.viewBadge}>{row.zoneCount ?? 0}</span>
                        </Link>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={styles.viewLink}
                          onClick={() => handleViewHolidayTypes(row)}
                        >
                          <Gift size={14} aria-hidden />
                          <span>View holiday types</span>
                        </button>
                      </td>
                      <td className={styles.rowActions}>
                        <button onClick={() => handleEdit(row)}>Edit</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              </table>
            </div>

            <div className={styles.footerBar}>
              <div className={styles.pagination}>
                <span className={styles.paginationInfo}>
                  Page {pagination?.page ?? page} of {pagination?.totalPages || 1} ({pagination?.total ?? 0} total)
                </span>
                {(pagination?.totalPages ?? 1) > 1 && (
                  <div className={styles.pageNumbers}>
                    {pageNumbers.map((p, idx) =>
                      p === "…" ? (
                        <span key={`ellipsis-${idx}`} className={styles.ellipsis}>…</span>
                      ) : (
                        <button
                          key={p}
                          className={`${styles.pageBtn} ${Number(p) === Number(page) ? styles.pageBtnActive : ""}`}
                          onClick={() => {
                            setPage(p);
                            setPendingChanges({});
                          }}
                        >
                          {p}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                className={styles.saveChangesBtn}
                onClick={handleSaveToggles}
                disabled={!hasPendingChanges || saveLoading}
              >
                {toggleSaveLoading ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}

      <AddCountryModal
        open={showModal}
        onClose={() => setShowModal(false)}
        mode={mode}
        initialData={selectedRow}
        onSave={handleSave}
        saveLoading={saveLoading}
      />

      <CountryHolidayTypesModal
        open={!!holidayTypesRow}
        onClose={() => setHolidayTypesRow(null)}
        country={holidayTypesRow}
        onSave={refetch}
      />
    </div>
  );
};

export default CountryManager;